import React from 'react';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import DeleteConfirmationModal from '../../../shared/components/modals/DeleteConfirmationModal';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import api from '../../../services/api';

import PPTTopNav from '../components/PPTTopNav';
import PPTOutlineSidebar from '../components/PPTOutlineSidebar';
import PPTSlideEditor from '../components/PPTSlideEditor';
import ExportModal from '../../rfp/shared/components/ExportModal';
import SaveCheckpointModal from '../components/SaveCheckpointModal';
import VersionHistoryPanel from '../components/VersionHistoryPanel';
import { getPptVersions } from '../utils/pptVersionService';
import { usePPTCustomization } from '../hooks/usePPTCustomization';
import './PPTCustomization.css';

const PPTCustomization = ({ onBack }) => {
    const { rfpId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [rfpData, setRfpData] = React.useState(null);
    const [toast, setToast] = React.useState(null);
    const [showExportModal, setShowExportModal] = React.useState(false);
    const [showSaveCheckpointModal, setShowSaveCheckpointModal] = React.useState(false);
    const [showVersionHistory, setShowVersionHistory] = React.useState(false);
    const [currentVersionNumber, setCurrentVersionNumber] = React.useState(6);
    const [brandingData, setBrandingData] = React.useState({
        clientName: 'Exalogic Consulting',
        clientLogo: null,
        clientLogoUrl: '',
        primaryColor: '#D63F15',
        secondaryColor: '#64748B'
    });

    const toastTimerRef = React.useRef(null);

    const showToast = React.useCallback((message, type = 'success', options = {}) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

        const toastObj = typeof options === 'boolean' ? { message, type, persistent: options } : { message, type, ...options };
        setToast(toastObj);

        // When a draft is saved successfully, follow up with persistent warning toast
        if (type === 'success' && (message.toLowerCase().includes('draft') || message.toLowerCase().includes('saved'))) {
            toastTimerRef.current = setTimeout(() => {
                setToast({
                    message: 'Please save as checkpoint to create new version',
                    type: 'warning',
                    persistent: true
                });
            }, 1800);
            return;
        }

        if (!toastObj.persistent) {
            toastTimerRef.current = setTimeout(() => setToast(null), 4000);
        }
    }, []);

    const fetchRFPDetails = React.useCallback(async () => {
        try {
            const res = await api.get(`/rfp/${rfpId}`);
            if (res.data) {
                setRfpData(res.data);
            }
        } catch (error) {
            console.error('Error fetching RFP details for TopNav:', error);
        }
    }, [rfpId]);

    React.useEffect(() => {
        if (rfpId) {
            fetchRFPDetails();
            getPptVersions(rfpId).then(data => {
                if (Array.isArray(data) && data.length > 0) {
                    const maxV = Math.max(...data.map(v => v.version_number || 1));
                    setCurrentVersionNumber(maxV);
                }
            }).catch(err => {
                console.log('No prior version history found, initializing version badge.');
            });
        }
    }, [rfpId, fetchRFPDetails]);

    const executeStageTransition = async (stageValue, notes = '') => {
        try {
            let stageCode = stageValue;
            if (stageValue === 'Submitted' || stageValue === 4 || stageValue === '4') {
                stageCode = 5; // SUBMITTED_TO_CLIENT code is 5 in STAGE_MAP / backend
            } else if (stageValue === 'Client Accepted' || stageValue === 'Proposal Accepted' || stageValue === 'accepted') {
                stageCode = 6;
            } else if (stageValue === 'Client Rejected' || stageValue === 'Proposal Rejected' || stageValue === 'rejected') {
                stageCode = 7;
            }

            const response = await api.patch(`/rfp/${rfpId}/proposal-stage`, {
                stage: stageCode,
                notes: notes || `Transitioned to ${stageValue}`
            });

            if (response.status === 200) {
                await fetchRFPDetails();
                const successMsg = response.data?.message || `Stage successfully updated to ${stageValue === 5 || stageValue === 4 ? 'RFP Submitted' : stageValue}!`;
                showToast(successMsg, 'success');
            }
        } catch (error) {
            console.error('Error updating stage:', error);
            const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to update stage. Please try again.';
            showToast(errorMsg, 'error');
        }
    };

    const {
        isEditMode, setIsEditMode, expandedSections, setExpandedSections, sections, setSections,
        activeSectionId, setActiveSectionId, activeTitleId, setActiveTitleId, isLoading,
        generatingSlideId, setGeneratingSlideId, isSidebarCollapsed, setIsSidebarCollapsed,
        showPreview, setShowPreview, isCreatingNewSection, setIsCreatingNewSection,
        newSectionForm, setNewSectionForm, isCreatingNewSlide, setIsCreatingNewSlide,
        creatingInSectionId, setCreatingInSectionId, newSlideForm, setNewSlideForm,
        showDeleteModal, setShowDeleteModal, targetToDelete, setTargetToDelete,
        activeDragId, setActiveDragId, showGenerateModal, setShowGenerateModal,
        selectedLogo, setSelectedLogo, isDownloading, setIsDownloading,
        isSaving, setIsSaving, previewImageUrl, setPreviewImageUrl,
        isPreviewLoading, setIsPreviewLoading, originalSlides, setOriginalSlides,
        generationSuccessMessage, setGenerationSuccessMessage,
        activeSection, activeTitle, toggleSection, selectTitle, handlePreviewSlide,
        handleAddSection, handleSaveNewSection, handleAddTitle, handleConfirmCreateSlide,
        handleUpdateSlide, handleUpdateSection, handleDownloadPPT, handleDeleteSection,
        handleDeleteTitle, confirmDeleteAction, handleGenerateSlide, handleRefreshSlide, handleFieldChange,
        handleSaveSlideContent,
        handleDragStart, handleDragOver, handleDragEnd,
        draftCount, setDraftCount
    } = usePPTCustomization(rfpId, location, showToast);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    if (isLoading) return <div className="ppt-loading-view"><CustomLoader size={48} /><p>Loading PPT Configuration...</p></div>;

    return (
        <div className="ppt-redesign-container" style={{
            '--brand-primary': brandingData.primaryColor,
            '--brand-secondary': brandingData.secondaryColor
        }}>
            <PPTTopNav
                onBack={onBack} navigate={navigate}
                setShowExportModal={setShowExportModal}
                rfpId={rfpId}
                rfpData={rfpData}
                executeStageTransition={executeStageTransition}
                fetchRFPDetails={fetchRFPDetails}
                showToast={showToast}
                brandingData={brandingData}
                onApplyBranding={(newData) => {
                    setBrandingData(newData);
                    showToast('Branding settings applied successfully!', 'success');
                }}
                onSaveCheckpoint={() => setShowSaveCheckpointModal(true)}
                onVersionHistory={() => setShowVersionHistory(true)}
                currentVersionNumber={currentVersionNumber || 6}
                draftCount={draftCount}
            />

            <main className="ppt-workspace-layout">
                <div className={`ppt-workspace-inner ${showPreview ? 'with-preview' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                    <PPTOutlineSidebar
                        isSidebarCollapsed={isSidebarCollapsed} setIsSidebarCollapsed={setIsSidebarCollapsed}
                        sections={sections} setSections={setSections}
                        activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId}
                        activeTitleId={activeTitleId} expandedSections={expandedSections}
                        toggleSection={toggleSection} handleAddSection={handleAddSection}
                        handleAddTitle={handleAddTitle} handleUpdateSection={handleUpdateSection}
                        selectTitle={selectTitle} sensors={sensors}
                        handleDragStart={handleDragStart} handleDragOver={handleDragOver}
                        handleDragEnd={handleDragEnd} activeDragId={activeDragId}
                        setIsCreatingNewSlide={setIsCreatingNewSlide}
                        setIsCreatingNewSection={setIsCreatingNewSection}
                        handleDeleteTitle={handleDeleteTitle}
                        handleDeleteSection={handleDeleteSection}
                    />

                    <PPTSlideEditor
                        isCreatingNewSlide={isCreatingNewSlide} isCreatingNewSection={isCreatingNewSection}
                        activeTitle={activeTitle} activeSection={activeSection}
                        activeSectionId={activeSectionId} activeTitleId={activeTitleId}
                        sections={sections} newSlideForm={newSlideForm} setNewSlideForm={setNewSlideForm}
                        newSectionForm={newSectionForm} setNewSectionForm={setNewSectionForm}
                        isSaving={isSaving} handleConfirmCreateSlide={handleConfirmCreateSlide}
                        setIsCreatingNewSlide={setIsCreatingNewSlide}
                        handleSaveNewSection={handleSaveNewSection}
                        setIsCreatingNewSection={setIsCreatingNewSection}
                        handleFieldChange={handleFieldChange} handleUpdateSlide={handleUpdateSlide}
                        handleDeleteTitle={handleDeleteTitle} handleGenerateSlide={handleGenerateSlide} handleRefreshSlide={handleRefreshSlide}
                        handleAddTitle={handleAddTitle} generatingSlideId={generatingSlideId}
                        previewImageUrl={previewImageUrl} setPreviewImageUrl={setPreviewImageUrl}
                        isPreviewLoading={isPreviewLoading} handlePreviewSlide={handlePreviewSlide}
                        showPreview={showPreview} creatingInSectionId={creatingInSectionId}
                        originalSlides={originalSlides}
                        handleSaveSlideContent={handleSaveSlideContent}
                    />
                </div>
            </main>

            {showDeleteModal && (
                <DeleteConfirmationModal
                    project={{ title: targetToDelete?.title }}
                    customTitle={`Are you sure you want to delete this ${targetToDelete?.type === 'section' ? 'section' : 'slide'}: "${targetToDelete?.title}"?`}
                    onClose={() => setShowDeleteModal(false)}
                    onDeleteConfirm={confirmDeleteAction}
                />
            )}

            {showExportModal && (
                <ExportModal
                    rfpId={rfpId}
                    isManager={true}
                    onClose={() => setShowExportModal(false)}
                    onPPTClick={handleDownloadPPT}
                />
            )}

            {showSaveCheckpointModal && (
                <SaveCheckpointModal
                    rfpId={rfpId}
                    onClose={() => setShowSaveCheckpointModal(false)}
                    currentVersionNumber={currentVersionNumber || 6}
                    draftCount={draftCount}
                    onSuccess={(result, nextVer) => {
                        setShowSaveCheckpointModal(false);
                        const newVer = result?.version_number || nextVer || (currentVersionNumber ? currentVersionNumber + 1 : 7);
                        setCurrentVersionNumber(newVer);
                        setDraftCount(0);
                        showToast(`Version ${newVer} created successfully!`, 'success');
                    }}
                />
            )}

            {showVersionHistory && (
                <VersionHistoryPanel
                    rfpId={rfpId}
                    onClose={() => setShowVersionHistory(false)}
                    currentVersionNumber={currentVersionNumber}
                    onRestoreSuccess={(newVersionNumber) => {
                        setCurrentVersionNumber(newVersionNumber);
                        showToast('Version restored successfully. Refreshing...', 'success');
                        // Refetch PPT config to reflect restored version
                        fetchRFPDetails();
                    }}
                />
            )}

            <SuccessToast toast={toast} onClose={() => setToast(null)} />

            {isSaving && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(15, 23, 42, 0.3)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 99999
                }}>
                    <div style={{
                        background: '#ffffff',
                        padding: '24px 32px',
                        borderRadius: '12px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '12px',
                        border: '1px solid #E2E8F0',
                        minWidth: '200px'
                    }}>
                        <CustomLoader size={28} color="#FF5526" />
                        <span style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1E293B',
                            letterSpacing: '0.02em'
                        }}>
                            Saving...
                        </span>
                    </div>
                </div>
            )}

            {generationSuccessMessage && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 100000
                }}>
                    <div style={{
                        background: '#ffffff',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                        display: 'flex', flexDirection: 'column',
                        gap: '20px', border: '1px solid #F1F5F9',
                        maxWidth: '460px', width: '90%'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            width: '100%'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#FF552615',
                                color: '#FF5526',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '18px',
                                flexShrink: 0
                            }}>
                                ✨
                            </div>
                            <p style={{
                                margin: 0,
                                fontSize: '14px',
                                color: '#1E293B',
                                lineHeight: '1.5',
                                fontWeight: '600'
                            }}>
                                {generationSuccessMessage}
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%'
                        }}>
                            <button
                                onClick={() => setGenerationSuccessMessage(null)}
                                style={{
                                    padding: '8px 20px',
                                    background: '#FF5526',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 12px rgba(240, 74, 35, 0.15)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(240, 74, 35, 0.25)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'none';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(240, 74, 35, 0.15)';
                                }}
                            >
                                View Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PPTCustomization;
